<template>
    <div class="box-page">
        <!-- 顶部栏 -->
        <header class="top-nav">
            <button type="button" class="back-btn" aria-label="Volver" @click="$router.back()">
                <van-icon name="arrow-left" />
            </button>
            <h1 class="nav-title">{{ getLabel('box.title', 'Cofre del Tesoro') }}</h1>
        </header>

        <main class="page-shell">
            <!-- Evento Diario 卡 -->
            <section class="hero-card">
                <div class="hero-top">
                    <span class="hero-tag">
                        <van-icon name="gold-coin-o" />
                        {{ getLabel('box.dailyEvent', 'Evento Diario') }}
                    </span>
                    <div class="hero-actions">
                        <button type="button" class="refresh-btn" aria-label="Actualizar" @click="loadData">
                            <van-icon name="replay" />
                        </button>
                        <button type="button" class="invite-top-btn" @click="copyInviteLink">
                            {{ getLabel('box.invite', 'Invitar') }}
                            <van-icon name="arrow" />
                        </button>
                    </div>
                </div>

                <p class="hero-sub">{{ getLabel('box.todayInvites', 'Invitaciones de compra de hoy') }}</p>
                <div class="hero-count">
                    <b>{{ todayInvites }}</b>
                    <span>{{ getLabel('box.people', 'personas') }}</span>
                </div>

                <div class="hero-divider"></div>

                <p class="hero-desc">
                    {{ getLabel('box.desc1', 'Invita a un amigo a unirse y alquilar equipo hoy para abrir un cofre del tesoro') }}<br>
                    {{ getLabel('box.desc2', 'Cuantas más personas invites hoy, mayores serán las recompensas del cofre') }}<br>
                    {{ getLabel('box.desc3', 'Se reinicia diariamente a las 00:00') }}
                </p>
            </section>

            <!-- 宝箱列表 -->
            <h2 class="section-title">
                <van-icon name="gold-coin-o" />
                {{ getLabel('box.chestList', 'Cofres del Tesoro') }}
            </h2>

            <section class="chest-grid">
                <article
                    v-for="chest in chests"
                    :key="chest.level"
                    class="chest-card"
                    :class="{ unlocked: chest.status === 1 || todayInvites >= chest.need, claimed: chest.status === 2 }"
                    @click="claimChest(chest)"
                >
                    <span class="chest-badge">
                        <van-icon name="friends-o" />
                        {{ chest.need }}
                    </span>
                    <div class="chest-art">
                        <div class="chest-body">
                            <div class="chest-lid"></div>
                            <div class="chest-lock">
                                <van-icon name="lock" v-if="chest.status === 0 && todayInvites < chest.need" />
                                <van-icon name="passed" v-else-if="chest.status !== 2" />
                                <van-icon name="gold-coin-o" v-else />
                            </div>
                        </div>
                    </div>
                    <div class="chest-amount">
                        <b>{{ chest.reward }}</b>
                        <span>INR</span>
                    </div>
                    <div class="chest-progress">
                        <template v-if="chest.status === 2">
                            {{ getLabel('box.claimed', 'Reclamado') }}
                        </template>
                        <template v-else-if="chest.status === 1 || todayInvites >= chest.need">
                            {{ getLabel('box.tapToClaim', 'Toca para reclamar') }}
                        </template>
                        <template v-else>
                            {{ Math.min(todayInvites, chest.need) }}/{{ chest.need }} {{ getLabel('box.people', 'personas') }}
                        </template>
                    </div>
                </article>
            </section>

            <!-- 规则 -->
            <section class="rules-card">
                <h2 class="rules-title">{{ getLabel('box.rules', 'Reglas') }}</h2>
                <ul class="rules-list">
                    <li v-for="(rule, i) in rules" :key="i">{{ rule }}</li>
                </ul>
            </section>

            <!-- 底部邀请按钮 -->
            <button type="button" class="invite-main-btn" @click="copyInviteLink">
                <van-icon name="friends-o" />
                {{ getLabel('box.inviteFriends', 'Invitar Amigos') }}
            </button>
        </main>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Toast } from "vant";

Vue.use(Icon);
Vue.use(Toast);

export default {
    name: "treasureBox",
    data() {
        return {
            todayInvites: 0,
            inviteLink: "",
            chests: [],
            defaultChests: (() => {
                const list = [];
                for (let i = 1; i <= 12; i++) {
                    list.push({ level: i, need: i, reward: 20 + i * 10, status: 0 });
                }
                return list;
            })(),
            rules: [
                this.getLabel('box.rule1', 'This event is open to all employees, new and existing'),
                this.getLabel('box.rule2', 'Successfully inviting 1 direct level 1 member on the day unlocks the possibility to open the chest'),
                this.getLabel('box.rule3', 'Each level of vault has its own guest threshold; the exact requirement is indicated below each chest'),
                this.getLabel('box.rule4', 'Once the relevant conditions are met, the safe can be opened and the rewards will be delivered automatically by the system'),
                this.getLabel('box.rule5', 'Chest reward amounts will be automatically credited to your personal account balance'),
                this.getLabel('box.rule6', 'Invitation counting restarts daily at 00:00; Chests not opened that day will expire automatically.'),
                this.getLabel('box.rule7', 'The platform reserves the right of final interpretation of this event'),
                this.getLabel('box.rule8', 'Your subordinate must contact your exclusive manager to complete the induction training and registration of the new employee before they can receive the reward from the vault'),
            ],
        };
    },
    mounted() {
        this.loadData();
    },
    methods: {
        getLabel(key, fallback) {
            return this.$te(key) ? this.$t(key) : fallback;
        },
        loadData() {
            Fetch("/user/chestInfo").then((r) => {
                const d = (r && r.data) || {};
                this.todayInvites = Number(d.today_invite ?? d.today_invites ?? 0);
                this.chests = Array.isArray(d.chests) && d.chests.length ? d.chests : this.defaultChests;
            }).catch(() => {
                this.chests = this.defaultChests;
            });
            Fetch("/user/myTeam").then((r) => {
                const d = (r && r.data) || {};
                this.inviteLink = d.share_link || "";
            }).catch(() => {});
        },
        claimChest(chest) {
            if (chest.status === 2) return;
            if (chest.status !== 1 && this.todayInvites < chest.need) {
                Toast(this.getLabel('box.notEnough', 'You need more invitations'));
                return;
            }
            Fetch("/user/claimChest", { level: chest.level }).then(() => {
                chest.status = 2;
                Toast(this.getLabel('box.claimSuccess', 'Safe claimed!'));
            }).catch(() => {});
        },
        copyInviteLink() {
            const link = this.inviteLink || (window.location.origin + window.location.pathname.replace(/\/$/, "") + "#/register?invite=");
            const value = String(link || "");
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(value).then(() => {
                    Toast(this.getLabel('box.copyOk', 'Link copied'));
                }).catch(() => {
                    this.fallbackCopy(value);
                });
                return;
            }
            this.fallbackCopy(value);
        },
        fallbackCopy(text) {
            const input = document.createElement("textarea");
            input.value = text;
            input.style.position = "fixed";
            input.style.opacity = "0";
            document.body.appendChild(input);
            input.select();
            try {
                document.execCommand("copy");
                Toast(this.getLabel('box.copyOk', 'Link copied'));
            } catch (e) {
                Toast(this.getLabel('box.copyFail', 'Error copying'));
            }
            document.body.removeChild(input);
        },
    },
};
</script>

<style lang="less" scoped>
.box-page {
    min-height: 100vh;
    background:
        radial-gradient(circle at 50% 0%, rgba(20, 80, 100, 0.3), transparent 50%),
        #05070f;
    color: #fff;
}

.top-nav {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 14px;
}

.back-btn {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid rgba(90, 130, 200, 0.3);
    border-radius: 50%;
    background: rgba(20, 35, 65, 0.6);
    color: #cdd8ee;
    display: grid;
    place-items: center;
    cursor: pointer;
}

.back-btn .van-icon { font-size: 18px; }

.nav-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #eef3ff;
}

.page-shell {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 0 14px 30px;
    box-sizing: border-box;
}

/* ---------- Evento Diario ---------- */
.hero-card {
    position: relative;
    padding: 16px;
    margin-bottom: 18px;
    border: 1px solid rgba(45, 208, 230, 0.4);
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(10, 40, 55, 0.9), rgba(8, 25, 40, 0.9));
    box-shadow: 0 0 20px rgba(45, 208, 230, 0.12);
    overflow: hidden;
}

.hero-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.hero-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #2fd0e6;
    font-size: 14px;
    font-weight: 700;
}

.hero-tag .van-icon { font-size: 16px; }

.hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.refresh-btn {
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #2fd0e6;
    display: grid;
    place-items: center;
    cursor: pointer;
}

.refresh-btn .van-icon { font-size: 18px; }

.invite-top-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid rgba(45, 208, 230, 0.5);
    border-radius: 8px;
    background: transparent;
    color: #2fd0e6;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
}

.invite-top-btn .van-icon { font-size: 14px; }

.hero-sub {
    margin: 0 0 4px;
    color: #8fa8c0;
    font-size: 12px;
}

.hero-count {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.hero-count b {
    color: #2fd0e6;
    font-size: 36px;
    font-weight: 800;
    line-height: 1.1;
}

.hero-count span {
    color: #8fa8c0;
    font-size: 14px;
}

.hero-divider {
    height: 1px;
    margin: 14px 0;
    background: rgba(45, 208, 230, 0.2);
}

.hero-desc {
    margin: 0;
    color: #8fa8c0;
    font-size: 12px;
    line-height: 1.7;
}

/* ---------- 区块标题 ---------- */
.section-title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 14px;
    color: #e6edfb;
    font-size: 15px;
    font-weight: 700;
}

.section-title .van-icon {
    color: #2fd0e6;
    font-size: 16px;
}

/* ---------- 宝箱网格 ---------- */
.chest-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.chest-card {
    position: relative;
    padding: 16px 12px 12px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(12, 25, 45, 0.9), rgba(6, 14, 28, 0.95));
    text-align: center;
    overflow: hidden;
}

.chest-card.unlocked {
    border-color: rgba(45, 208, 230, 0.5);
    box-shadow: 0 0 12px rgba(45, 208, 230, 0.15);
    cursor: pointer;
}

.chest-card.claimed {
    opacity: 0.55;
}

.chest-card.claimed .chest-progress {
    background: rgba(45, 208, 230, 0.25);
    color: #2fd0e6;
}

.chest-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border: 1px solid rgba(90, 130, 200, 0.4);
    border-radius: 999px;
    background: rgba(10, 25, 45, 0.8);
    color: #8fa8c0;
    font-size: 11px;
    font-weight: 600;
}

.chest-badge .van-icon { font-size: 12px; }

.chest-art {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 70px;
    margin-bottom: 10px;
}

.chest-body {
    position: relative;
    width: 56px;
    height: 46px;
}

.chest-lid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 18px;
    border-radius: 6px 6px 4px 4px;
    background: linear-gradient(180deg, #3a3f4a, #2a2e38);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.chest-body::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 30px;
    border-radius: 4px;
    background: linear-gradient(180deg, #2a2e38, #1a1d24);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.chest-lock {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -30%);
    z-index: 2;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(212, 160, 23, 0.3);
    display: grid;
    place-items: center;
    color: #ffd25e;
}

.chest-lock .van-icon { font-size: 12px; }

.chest-card.unlocked .chest-body::after {
    background: linear-gradient(180deg, #4a3a1a, #2a2010);
}

.chest-card.unlocked .chest-lid {
    background: linear-gradient(180deg, #8a6a2a, #5a451a);
}

.chest-amount {
    margin-bottom: 10px;
}

.chest-amount b {
    color: #fff;
    font-size: 20px;
    font-weight: 800;
}

.chest-amount span {
    color: #8fa8c0;
    font-size: 12px;
    margin-left: 3px;
}

.chest-progress {
    padding: 7px 0;
    border-radius: 8px;
    background: rgba(20, 60, 75, 0.6);
    color: #c5d8e8;
    font-size: 11px;
    font-weight: 600;
}

/* ---------- 规则 ---------- */
.rules-card {
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: rgba(10, 20, 40, 0.7);
}

.rules-title {
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 700;
    color: #eef3ff;
}

.rules-list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.rules-list li {
    position: relative;
    padding-left: 16px;
    margin-bottom: 10px;
    font-size: 12px;
    line-height: 1.6;
    color: #9fb0cc;
}

.rules-list li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 7px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #2fd0e6;
}

/* ---------- 底部按钮 ---------- */
.invite-main-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 50px;
    border: 0;
    border-radius: 12px;
    background: linear-gradient(90deg, #2fd0e6, #17a2c4);
    color: #04222b;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(45, 208, 230, 0.3);
}

.invite-main-btn .van-icon { font-size: 20px; }
</style>
